/**
 * The Tailwind class tokens that `useThemeColors().classes` exposes.
 *
 * This map is the SINGLE source of truth for the class-token vocabulary. The
 * `ThemeColors['classes']` type is derived from it via `typeof`, so a token
 * cannot exist in the runtime map without existing in the type, or vice versa.
 *
 * ---------------------------------------------------------------------------
 * EVERY VALUE HERE RESOLVES TO A DESIGN TOKEN
 * ---------------------------------------------------------------------------
 *
 * A token names a ROLE and the role resolves to a CSS variable declared in
 * `design-tokens.ts`. It used to name a role and hardcode two greys:
 *
 *     before:  bgPrimary: 'bg-white dark:bg-gray-800'
 *     after:   bgPrimary: 'bg-surface'
 *
 * Two things follow from that. The `dark:` variants are gone — the variable
 * flips with the theme, so a token can no longer say one thing in light mode
 * and drift in dark. And changing the design is a values edit in one file
 * instead of 261 string edits re-verified in two modes.
 *
 * **Do not reintroduce a raw Tailwind palette colour here** (`bg-gray-800`,
 * `text-blue-600`, `border-red-500`). A contract test fails the build if you
 * do. If no existing role fits, add the role to `design-tokens.ts` — that is
 * the decision point, and it should be a deliberate one.
 *
 * Names in this map are the OLD vocabulary and are deliberately unchanged, so
 * none of the ~13,000 call sites had to move. They do not line up 1:1 with the
 * variable names; see the mapping note in `design-tokens.ts`. The plan retires
 * this map entirely once call sites use the utilities directly.
 */

export const tailwindClasses = {
	// Text colors
	textPrimary: 'text-default',
	textSecondary: 'text-muted',
	textTertiary: 'text-faint',
	textMuted: 'text-subtle',
	textInverse: 'text-on-fill',
	textDanger: 'text-danger',
	textError: 'text-danger-strong',
	textSuccess: 'text-success',
	textWarning: 'text-warning-alt',
	textInfo: 'text-accent',
	textLink: 'text-accent',
	textLinkHover: 'hover:text-accent-hover',
	textAccent: 'text-accent',
	textWhite: 'text-on-fill',
	// Text on a solid accent-colored background (e.g. active filter buttons).
	textOnPrimary: 'text-on-accent',

	// Status text colors
	statusSuccess: 'text-success',
	statusWarning: 'text-warning',
	statusError: 'text-danger',
	statusInfo: 'text-accent',

	// Backgrounds
	bgPrimary: 'bg-surface',
	bgSecondary: 'bg-canvas',
	bgTertiary: 'bg-sunken',
	bgContainer: 'bg-surface',
	bgInverse: 'bg-inverse',
	bgDefault: 'bg-surface',
	bgSubtle: 'bg-canvas',

	// Interactive backgrounds
	bgPrimaryHover: 'hover:bg-surface-hover',
	bgSecondaryHover: 'hover:bg-subtle',
	hoverButton: 'hover:bg-subtle',
	bgHover: 'hover:bg-subtle',
	bgMuted: 'bg-control-strong',
	bgOverlay: 'bg-scrim',
	divider: 'divide-muted',
	textOnDanger: 'text-danger-strong',
	backgroundBase: 'bg-surface',
	bgMenuItemActive: 'bg-subtle',

	// Status backgrounds
	bgSuccess: 'bg-success-tint',
	bgWarning: 'bg-warning-tint',
	bgDanger: 'bg-danger-tint',
	bgDangerHover: 'hover:bg-danger-tint-hover',
	bgInfo: 'bg-accent-tint',
	bgInfoHover: 'hover:bg-accent-tint-hover',
	bgInfoLight: 'bg-accent-tint-subtle',
	bgError: 'bg-danger-emphasis',
	bgErrorLight: 'bg-danger-tint-subtle',

	// Borders
	borderDefault: 'border-default',
	borderStrong: 'border-strong',
	borderLight: 'border-subtle',
	borderError: 'border-danger',
	borderWarning: 'border-warning',
	borderDisabled: 'border-muted',
	borderInfo: 'border-accent',
	borderSuccess: 'border-success',
	borderDanger: 'border-danger',
	borderBase: 'border-default',
	borderPrimary: 'border-accent',
	borderMuted: 'border-strong',
	borderHover: 'hover:border-strong',

	// Rings
	ringPrimary: 'ring-accent',
	ringSuccess: 'ring-success',
	ringError: 'ring-danger',

	// Solid status fill
	bgSuccessSolid: 'bg-success-emphasis',

	// Buttons
	primaryButton:
		'bg-accent hover:bg-accent-hover text-on-accent !text-on-accent',
	secondaryButton: 'bg-control hover:bg-control-strong text-default',
	dangerButton:
		'bg-danger-solid hover:bg-danger-solid-hover text-on-fill !text-on-fill',
	// Approval / match-confirmation action. Distinct from `primaryButton`
	// because "confirm this is correct" is not the same as "submit".
	successButton:
		'bg-success-solid hover:bg-success-solid-hover text-on-success !text-on-success',
	outlineButton: 'border border-strong text-muted hover:bg-surface-hover',
	ghostButton: 'text-muted hover:bg-subtle',
	buttonGroupSelected: 'bg-accent-tint text-accent-tint-fg border-accent',

	// Inputs
	inputDefault: 'bg-surface border-strong text-default',

	// Tables
	tableHeader: 'bg-sunken',
	tableHeaderCell: 'text-default font-semibold',
	tableRow: 'bg-surface hover:bg-surface-hover',
	tableRowStripe: 'even:bg-row-stripe',
	tableCell: 'text-default',
	tableDivider: 'divide-muted',

	// Chart tooltip
	chartTooltipBackground: 'bg-tooltip-chart',
	chartTooltipBorder: 'border-muted',
	chartTooltipText: 'text-muted',
	chartTooltipLabel: 'text-default',
	chartTooltipShadow: 'shadow-2xl',
	chartTooltipContainer: 'rounded-xl backdrop-blur-sm',

	// Tooltip. Dark in BOTH themes — a tooltip reads as an overlay, not a panel.
	tooltipBackground: 'bg-tooltip',
	tooltipText: 'text-on-fill',
	tooltipShadow: 'shadow-2xl',

	// Navigation rail. Dark in BOTH themes by design, which is why these tokens
	// resolve to `--nav-*` variables that carry the same value twice rather than
	// to the surface/foreground ramp.
	navBackground: 'bg-nav',
	navBorder: 'border-nav',
	navItemText: 'text-nav',
	navItemTextActive: 'text-nav-active',
	navItemBgHover: 'bg-nav-hover',
	navItemBgActive: 'bg-nav-hover',
	navGroupText: 'text-nav-muted',
	navIconDefault: 'text-nav-muted',
	navIconActive: 'text-nav-active',
	navSeparator: 'bg-nav-hover',
	navUserMenuBg: 'bg-nav-hover',
	navUserMenuHover: 'bg-nav-hover-strong',
	navUserMenuText: 'text-nav-strong',
	navBadgeBg: 'bg-nav-badge',

	// Filter pills
	filterPill: 'bg-control',
	filterPillActive: 'bg-info-solid text-on-fill',
	filterPillText: 'text-muted',

	// Overlays
	backdropOverlay: 'bg-overlay backdrop-blur-sm',
	modalPanel: 'bg-surface',

	// Focus rings
	focusRingDefault:
		'focus:outline-none focus:ring-2 focus:ring-neutral focus:ring-offset-0',
	focusRingPrimary:
		'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-0',
	focusRingDanger:
		'focus:outline-none focus:ring-2 focus:ring-danger focus:ring-offset-0',
	focusRingSuccess:
		'focus:outline-none focus:ring-2 focus:ring-success focus:ring-offset-0',
	focusRingWarning:
		'focus:outline-none focus:ring-2 focus:ring-warning focus:ring-offset-0',
	focusRingNone: 'focus:outline-none',

	// Solid status badges — emphasis, not the default treatment.
	statusBadgeSuccess: 'bg-success-emphasis text-on-success',
	statusBadgeWarning: 'bg-warning-solid text-on-warning',
	statusBadgeError: 'bg-danger-emphasis text-on-fill',
	statusBadgeInfo: 'bg-info-solid text-on-fill',
	statusBadgeDefault: 'bg-neutral-solid text-on-fill',

	// Tinted status badges, outlined. The `*Light` suffix is the older name for
	// what the palette work calls the default (quiet) treatment.
	statusBadgeSuccessLight:
		'bg-success-tint text-success-tint-fg border border-success-tint',
	statusBadgeWarningLight:
		'bg-warning-tint-amber-soft text-warning-tint-fg border border-warning-tint',
	statusBadgeErrorLight:
		'bg-danger-tint text-danger-tint-fg border border-danger-tint',
	statusBadgeInfoLight:
		'bg-accent-tint text-accent-tint-fg border border-accent-tint',
	statusBadgeDefaultLight:
		'bg-neutral-tint text-neutral-tint-fg border border-neutral-tint',

	// Domain status colours.
	//
	// Reached through the `get*StatusClasses()` helpers in ThemeService rather
	// than by name at a call site, which is why a plain "find unused tokens"
	// pass reports them as dead. They are not — the helpers are the public API.
	statusOrderOpen: 'bg-info-solid text-on-fill',
	statusOrderProcessing: 'bg-warning-solid text-on-warning',
	statusOrderShipped: 'bg-transit-solid text-on-fill',
	statusOrderDelivered: 'bg-success-emphasis text-on-success',
	statusOrderCancelled: 'bg-danger-emphasis text-on-fill',
	statusOrderDefault: 'bg-neutral-solid text-on-fill',

	statusInvoiceOpen: 'bg-info-solid text-on-fill',
	statusInvoicePartial: 'bg-warning-solid text-on-warning',
	statusInvoicePaid: 'bg-success-emphasis text-on-success',
	statusInvoiceOverdue: 'bg-danger-solid-strong text-on-fill',
	statusInvoiceCancelled: 'bg-neutral-solid text-on-fill',
	statusInvoiceDefault: 'bg-neutral-solid text-on-fill',

	statusPaymentPaid: 'bg-success-solid text-on-success',
	statusPaymentPending: 'bg-warning-solid-strong text-on-warning',
	statusPaymentPartial: 'bg-accent text-on-accent',
	statusPaymentFailed: 'bg-danger-solid text-on-fill',
	statusPaymentRefunded: 'bg-refund-solid text-on-fill',
	statusPaymentDefault: 'bg-neutral-solid-alt text-on-fill',

	statusCampaignActive: 'bg-success-emphasis text-on-success',
	statusCampaignScheduled: 'bg-info-solid text-on-fill',
	statusCampaignPaused: 'bg-warning-solid text-on-warning',
	statusCampaignCompleted: 'bg-neutral-solid text-on-fill',
	statusCampaignFailed: 'bg-danger-emphasis text-on-fill',
	statusCampaignDefault: 'bg-neutral-solid text-on-fill',

	statusLotActive: 'bg-success-emphasis text-on-success',
	statusLotExpired: 'bg-danger-emphasis text-on-fill',
	statusLotQuarantine: 'bg-warning-solid text-on-warning',
	statusLotDefault: 'bg-neutral-solid text-on-fill',

	statusStockHigh: 'bg-success-emphasis text-on-success',
	statusStockMedium: 'bg-info-solid text-on-fill',
	statusStockLow: 'bg-warning-solid text-on-warning',
	statusStockCritical: 'bg-danger-emphasis text-on-fill',
	statusStockDefault: 'bg-neutral-solid text-on-fill',

	// Categorical chip palette. Cycled by `getChipColor(index)` — every entry is
	// reachable even though no call site names one, so none of these are dead.
	chipColor1: 'bg-chip-1 text-chip-1 border border-chip-1',
	chipColor2: 'bg-chip-2 text-chip-2 border border-chip-2',
	chipColor3: 'bg-chip-3 text-chip-3 border border-chip-3',
	chipColor4: 'bg-chip-4 text-chip-4 border border-chip-4',
	chipColor5: 'bg-chip-5 text-chip-5 border border-chip-5',
	chipColor6: 'bg-chip-6 text-chip-6 border border-chip-6',
	chipColor7: 'bg-chip-7 text-chip-7 border border-chip-7',
	chipColor8: 'bg-chip-8 text-chip-8 border border-chip-8',
	chipColor9: 'bg-chip-9 text-chip-9 border border-chip-9',
	chipColor10: 'bg-chip-10 text-chip-10 border border-chip-10',
	chipColor11: 'bg-chip-11 text-chip-11 border border-chip-11',
	chipColor12: 'bg-chip-12 text-chip-12 border border-chip-12',
	chipColor13: 'bg-chip-13 text-chip-13 border border-chip-13',
	chipColor14: 'bg-chip-14 text-chip-14 border border-chip-14',
	chipColor15: 'bg-chip-15 text-chip-15 border border-chip-15',
	chipColor16: 'bg-chip-16 text-chip-16 border border-chip-16',

	// Tinted badges — the default status treatment.
	badgePrimary: 'bg-accent-tint text-accent-tint-fg',
	badgeSecondary: 'bg-sunken text-muted',
	badgeSuccess: 'bg-success-tint text-success-tint-fg',
	badgeWarning: 'bg-warning-tint-amber text-warning-tint-fg',
	badgeDanger: 'bg-danger-tint text-danger-tint-fg',
	badgeInfo: 'bg-accent-tint text-accent-tint-fg',
	badgeDefault: 'bg-sunken text-muted',

	// Form fields
	formFieldBase: 'rounded-md shadow-sm transition-colors duration-200',
	formFieldDefault:
		'bg-surface border border-strong text-default placeholder-faint',
	formFieldFocus:
		'focus:border-focus focus:ring-2 focus:ring-accent-soft focus:outline-none',
	formFieldError:
		'bg-surface border border-danger text-default focus:border-danger focus:ring-2 focus:ring-danger-soft',
	formFieldDisabled:
		'bg-canvas border border-muted text-subtle cursor-not-allowed opacity-75',

	// Select
	selectMenu: 'bg-raised border border-muted shadow-lg',
	selectOption: 'bg-subtle text-default',
	selectOptionSelected: 'bg-accent-tint-subtle text-accent',

	// Agent chat
	agentMessageBg: 'bg-subtle',
	agentMessageText: 'text-default',
	userMessageBg: 'bg-accent',
	userMessageText: 'text-on-accent',
	dangerButtonText: 'text-on-fill',
} as const;

/**
 * The class-token vocabulary, derived from the map above.
 *
 * Deriving rather than declaring means the map and the type cannot disagree.
 * They used to be two hand-maintained lists and both drift directions had
 * occurred: a token on the type but missing from the map resolved to
 * `undefined` and React dropped the attribute, so the element silently
 * rendered unstyled; a token in the map but missing from the type made every
 * consumer a type error.
 */
export type ThemeClassTokens = typeof tailwindClasses;
